import {
	Button,
	Card,
	Group,
	Select,
	Skeleton,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { useSnapshot } from "valtio";
import { assistantUsageLogTexts } from "@/locales/assistant-usage-log";
import { i18nStore } from "@/store/i18n";
import {
	USAGE_LOG_STATUSES,
	USAGE_LOG_VENDORS,
	type UsageLogStatus,
	type UsageLogVendor,
} from "@/types/ai-assistant-usage-log";
import { statusLabel, vendorLabel } from "./usage-log.logic";
import { UsageLogSummary } from "./usage-log-summary";
import { UsageLogTable } from "./usage-log-table";
import {
	type AssistantUsageLogState,
	USAGE_LOG_RANGE_OPTIONS,
} from "./use-assistant-usage-log";

interface Props {
	log: AssistantUsageLogState;
}

const ALL = "";

/** Kartu "Log penggunaan penyedia" (admin): ringkasan vendor + tabel giliran berhalaman dengan filter. */
export function UsageLogCard({ log }: Props) {
	const { lang: uiLang } = useSnapshot(i18nStore);
	const lang = uiLang === "en" ? "en" : "id";
	const t = assistantUsageLogTexts[lang];
	const { rows, summary, filters, setFilters } = log;

	return (
		<Card withBorder radius="md" p="lg" data-usage-log-card>
			<Stack gap="md">
				<Stack gap={2}>
					<Title order={4}>{t.title}</Title>
					<Text size="sm" c="dimmed">
						{t.intro}
					</Text>
					<Text size="xs" c="dimmed">
						{t.privacyNote}
					</Text>
				</Stack>

				<Group gap="sm" align="flex-end">
					<Select
						label={t.rangeLabel}
						size="xs"
						w={160}
						allowDeselect={false}
						data={USAGE_LOG_RANGE_OPTIONS.map((days) => ({
							value: String(days),
							label: t.rangeOption(days),
						}))}
						value={String(filters.days)}
						onChange={(value) =>
							setFilters({ ...filters, days: Number(value ?? filters.days) })
						}
					/>
					<Select
						label={t.vendorLabel}
						size="xs"
						w={160}
						allowDeselect={false}
						data={[
							{ value: ALL, label: t.all },
							...USAGE_LOG_VENDORS.map((vendor) => ({
								value: vendor,
								label: vendorLabel(vendor, t),
							})),
						]}
						value={filters.vendor ?? ALL}
						onChange={(value) =>
							setFilters({
								...filters,
								vendor: value ? (value as UsageLogVendor) : undefined,
							})
						}
					/>
					<Select
						label={t.statusLabel}
						size="xs"
						w={160}
						allowDeselect={false}
						data={[
							{ value: ALL, label: t.all },
							...USAGE_LOG_STATUSES.map((status) => ({
								value: status,
								label: statusLabel(status, t),
							})),
						]}
						value={filters.status ?? ALL}
						onChange={(value) =>
							setFilters({
								...filters,
								status: value ? (value as UsageLogStatus) : undefined,
							})
						}
					/>
				</Group>

				{log.error && (
					<Text size="sm" c="red" data-usage-log-error>
						{log.error}
					</Text>
				)}

				{log.loading ? (
					<Stack gap="xs" data-usage-log-loading>
						<Text size="sm" c="dimmed">
							{t.loading}
						</Text>
						<Skeleton height={120} radius="sm" />
					</Stack>
				) : (
					<>
						{summary && <UsageLogSummary summary={summary} t={t} lang={lang} />}
						{rows && <UsageLogTable rows={rows} t={t} lang={lang} />}
						{log.nextCursor && (
							<Group>
								<Button
									variant="light"
									size="xs"
									loading={log.loadingMore}
									onClick={log.loadMore}
								>
									{t.loadMore}
								</Button>
							</Group>
						)}
					</>
				)}
			</Stack>
		</Card>
	);
}
